import { Container, Graphics, Sprite, Text, useTick } from "@pixi/react";
import {
  TextStyle,
  Texture,
  Graphics as GraphicsRaw,
  Container as ContainerType,
} from "pixi.js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  renderCurve as _renderCurve,
  createGradTexture,
  curveFunction,
  maskDraw as _drawMask,
  smoothen,
  interpolate,
  getHistoryItemColor,
} from "../../utils";
import type { dimensionType } from "../../@types";
import { GameStages } from "../../providers/SocketProvider";

// Extended breakpoints
type Breakpoint =
  | "mobileSmall"
  | "mobile"
  | "mobileLarge"
  | "tablet"
  | "tabletLarge"
  | "nestHub"
  | "nestHubMax"
  | "ipadPro"
  | "desktop"
  | "desktopLarge";

const getBreakpoint = (
  w: number,
  h: number = window.innerHeight,
): Breakpoint => {
  if (w >= 1024 && w <= 1030 && h <= 600) return "nestHub";
  if (w >= 1280 && w <= 1290 && h >= 780 && h <= 820) return "nestHubMax";
  if (w >= 1024 && w <= 1400 && h > 700 && h < 1100) return "ipadPro";

  if (w < 375) return "mobileSmall";
  if (w < 480) return "mobile";
  if (w < 600) return "mobileLarge";
  if (w < 920) return "tablet";
  if (w < 1024) return "tabletLarge";
  if (w < 1440) return "desktop";
  return "desktopLarge";
};

const clamp = (v: number, min: number, max: number) =>
  Math.max(min, Math.min(max, v));

const AppStage = ({
  payout,
  game_anim_status,
  dimension,
  pixiDimension, // unused but kept for compatibility
}: {
  payout: number;
  game_anim_status: GameStages;
  dimension: dimensionType;
  pixiDimension: dimensionType;
}) => {
  const tickRef = useRef(0);
  const pulseGraphRef = useRef(0);
  const crashOffset = useRef(0);

  const planeXRef = useRef(0);
  const posPlaneRef = useRef({ x: 0, y: 0 });

  const [currentFrame, setCurrentFrame] = useState(0);

  const [bp, setBp] = useState<Breakpoint>(() => {
    if (typeof window === "undefined") return "desktop";
    return getBreakpoint(window.innerWidth, window.innerHeight);
  });

  const [planeScale, setPlaneScale] = useState(0.3);
  const [pulseBase, setPulseBase] = useState(0.8);

  // refs
  const raysRef = useRef<ContainerType | null>(null);
  const revealRef = useRef<ContainerType | null>(null);
  const curveRef = useRef<GraphicsRaw | null>(null);
  const maskRef = useRef<GraphicsRaw | null>(null);
  const boardMaskRef = useRef<GraphicsRaw | null>(null);
  const planeSpriteRef = useRef<any>(null);

  // ✅ Animation toggle: local state driven by localStorage + window event
  const [animationEnabled, setAnimationEnabled] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    const v = window.localStorage.getItem("animationChecked");
    return v === null ? true : v === "true";
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handler = (evt: Event) => {
      const e = evt as CustomEvent<{ animationChecked?: boolean }>;
      if (typeof e.detail?.animationChecked === "boolean") {
        setAnimationEnabled(e.detail.animationChecked);
      } else {
        const v = window.localStorage.getItem("animationChecked");
        setAnimationEnabled(v === null ? true : v === "true");
      }
    };

    window.addEventListener("aviator:animation", handler);
    return () => window.removeEventListener("aviator:animation", handler);
  }, []);

  // per-breakpoint tuning
  const tweak = useMemo(() => {
    const map: Record<
      Breakpoint,
      {
        scale: number;
        anchor: { x: number; y: number };
        leadX: number;
        leadY: number;
        xOffsetRun: number;
        xOffsetWait: number;
        yOffset: number;
        graphOffset: { x: number; y: number };
        endX: number;
        endY: number;
        endMinX: number;
        planeMinY: number;
        planeMaxX: number;
        curveYScale: number;
      }
    > = {
      mobileSmall: {
        scale: 0.5,
        anchor: { x: 0.1, y: 0.53 },
        leadX: -10,
        leadY: 0,
        xOffsetRun: 6,
        xOffsetWait: 18,
        yOffset: 80,
        graphOffset: { x: 35, y: 0 },
        endX: 25,
        endY: -5,
        endMinX: 90,
        planeMinY: 40,
        planeMaxX: 0.85,
        curveYScale: 0.9,
      },
      mobile: {
        scale: 0.6,
        anchor: { x: 0.1, y: 0.53 },
        leadX: -10,
        leadY: 0,
        xOffsetRun: 6,
        xOffsetWait: 18,
        yOffset: 92,
        graphOffset: { x: 40, y: 0 },
        endX: 25,
        endY: -5,
        endMinX: 90,
        planeMinY: 50,
        planeMaxX: 0.85,
        curveYScale: 0.99,
      },
      mobileLarge: {
        scale: 0.55,
        anchor: { x: 0.1, y: 0.53 },
        leadX: -8,
        leadY: 0,
        xOffsetRun: 5,
        xOffsetWait: 16,
        yOffset: 85,
        graphOffset: { x: 35, y: 0 },
        endX: 22,
        endY: -3,
        endMinX: 90,
        planeMinY: 45,
        planeMaxX: 0.88,
        curveYScale: 0.85,
      },
      tablet: {
        scale: 0.45,
        anchor: { x: 0.08, y: 0.54 },
        leadX: 0,
        leadY: 0,
        xOffsetRun: 4,
        xOffsetWait: 14,
        yOffset: 68,
        graphOffset: { x: 15, y: -12 },
        endX: 25,
        endY: 0,
        endMinX: 90,
        planeMinY: 50,
        planeMaxX: 1.0,
        curveYScale: 0.88,
      },
      tabletLarge: {
        scale: 0.4,
        anchor: { x: 0.08, y: 0.54 },
        leadX: 0,
        leadY: 0,
        xOffsetRun: 4,
        xOffsetWait: 14,
        yOffset: 62,
        graphOffset: { x: 12, y: -12 },
        endX: 20,
        endY: 0,
        endMinX: 90,
        planeMinY: 55,
        planeMaxX: 0.9,
        curveYScale: 0.97,
      },
      nestHub: {
        scale: 0.28,
        anchor: { x: 0.07, y: 0.55 },
        leadX: 0,
        leadY: 0,
        xOffsetRun: 0,
        xOffsetWait: 10,
        yOffset: 45,
        graphOffset: { x: 6, y: 0 },
        endX: 18,
        endY: 10,
        endMinX: 90,
        planeMinY: 40,
        planeMaxX: 0.92,
        curveYScale: 1.0,
      },
      nestHubMax: {
        scale: 0.3,
        anchor: { x: 0.07, y: 0.55 },
        leadX: 0,
        leadY: 0,
        xOffsetRun: 0,
        xOffsetWait: 10,
        yOffset: 50,
        graphOffset: { x: 6, y: 0 },
        endX: 20,
        endY: 12,
        endMinX: 90,
        planeMinY: 50,
        planeMaxX: 0.92,
        curveYScale: 1.0,
      },
      ipadPro: {
        scale: 0.32,
        anchor: { x: 0.07, y: 0.55 },
        leadX: 0,
        leadY: 0,
        xOffsetRun: 0,
        xOffsetWait: 10,
        yOffset: 55,
        graphOffset: { x: 8, y: -5 },
        endX: 20,
        endY: 10,
        endMinX: 90,
        planeMinY: 60,
        planeMaxX: 0.9,
        curveYScale: 1.5,
      },
      desktop: {
        scale: 0.3,
        anchor: { x: 0.07, y: 0.55 },
        leadX: 0,
        leadY: 0,
        xOffsetRun: 0,
        xOffsetWait: 10,
        yOffset: 52,
        graphOffset: { x: 6, y: 0 },
        endX: 20,
        endY: 12,
        endMinX: 90,
        planeMinY: 50,
        planeMaxX: 0.92,
        curveYScale: 1.0,
      },
      desktopLarge: {
        scale: 0.3,
        anchor: { x: 0.07, y: 0.55 },
        leadX: 0,
        leadY: 0,
        xOffsetRun: 0,
        xOffsetWait: 10,
        yOffset: 52,
        graphOffset: { x: 6, y: 0 },
        endX: 20,
        endY: 12,
        endMinX: 90,
        planeMinY: 50,
        planeMaxX: 0.95,
        curveYScale: 1.0,
      },
    };

    return map[bp];
  }, [bp]);

  // Resize -> breakpoint + pulseBase
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleResize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      setBp(getBreakpoint(w, h));
      setPulseBase(interpolate(w, 400, 1920, 0.6, 0.8));
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => setPlaneScale(tweak.scale), [tweak.scale]);

  // build textures
  const planeFrames = useMemo(
    () =>
      Array.from({ length: 8 }, (_, i) =>
        Texture.from(`/plane/plane-${i + 1}.png`),
      ),
    [],
  );

  // optional: stop sprite animation when animation off (doesn't affect visibility)
  useEffect(() => {
    if (!animationEnabled) return;
    const interval = setInterval(() => {
      setCurrentFrame((prev) => (prev + 1) % planeFrames.length);
    }, 60);
    return () => clearInterval(interval);
  }, [planeFrames.length, animationEnabled]);

  // curve + mask draw funcs
  const renderCurve = useCallback(
    (g: GraphicsRaw) => _renderCurve(g, dimension),
    [dimension],
  );

  const curveMaskDraw = useCallback(
    (g: GraphicsRaw) =>
      _drawMask(g, { width: dimension.width, height: dimension.height }),
    [dimension.width, dimension.height],
  );

  const dotLeftBottom = useCallback(
    (g: GraphicsRaw) => _drawMask(g, { width: 1, height: 1 }),
    [],
  );

  // gradient
  const gradTexture = useMemo(() => createGradTexture(dimension), [dimension]);

  const gradTint = useMemo(() => {
    const numeric = (() => {
      if (typeof payout === "number") return payout;
      const parsed = parseFloat(String(payout).replace(/x$/i, ""));
      return Number.isNaN(parsed) ? 1 : parsed;
    })();

    const payoutStr = numeric.toFixed(2) + "x";
    const payoutColor = getHistoryItemColor(payoutStr);
    return parseInt(payoutColor.replace("#", ""), 16);
  }, [payout]);

  // assign masks
  useEffect(() => {
    const reveal = revealRef.current as any;
    const boardMask = boardMaskRef.current as any;
    if (reveal && boardMask) reveal.mask = boardMask;
  }, [dimension.width, dimension.height]);

  useEffect(() => {
    const curve = curveRef.current as any;
    const mask = maskRef.current as any;
    if (curve && mask) curve.mask = mask;
  });

  // reset on WAIT
  useEffect(() => {
    if (game_anim_status === GameStages.WAIT) {
      tickRef.current = 0;
      pulseGraphRef.current = 0;
      crashOffset.current = 0;
      planeXRef.current = 0;

      posPlaneRef.current = {
        x: interpolate(dimension.width, 400, 1920, 10, 5),
        y: dimension.height,
      };

      const c = curveRef.current;
      const m = maskRef.current;
      if (c) {
        c.position.set(0, dimension.height);
        c.scale.set(1, 1);
        c.pivot.set(0, dimension.height);
      }
      if (m) {
        m.position.set(0, 0);
        m.scale.set(0, 1);
      }

      const p = planeSpriteRef.current;
      if (p) p.position.set(0, 0);
    }
  }, [game_anim_status, dimension.width, dimension.height]);

  // background rotate on RUN (always rotates in RUN, toggle does NOT affect)
  useTick((delta) => {
    if (game_anim_status === GameStages.RUN && raysRef.current) {
      raysRef.current.rotation += 0.003 * delta;
    }
  });

  // MAIN TICK (plane math always runs; toggle only hides visuals)
  useTick((delta) => {
    const stage = game_anim_status;

    if (stage === GameStages.RUN) {
      tickRef.current += delta * 0.02;
      const amp = 0.06;
      pulseGraphRef.current = Math.sin(tickRef.current) * amp;

      const rawX = tickRef.current * 300;
      const smoothedX = smoothen(Math.min(rawX, dimension.width), {
        width: dimension.width,
        height: dimension.height,
      });
      planeXRef.current = smoothedX;
    }

    let crashX = 0;
    let crashY = 0;
    if (stage === GameStages.BLAST) {
      crashOffset.current += delta * 8;
      crashX = crashOffset.current * 8;
      crashY = crashOffset.current * 3;
    }

    const pulse = pulseGraphRef.current;
    const sxBase = pulseBase + pulse;

    const syBase =
      (1 - pulse) * (stage === GameStages.RUN ? tweak.curveYScale : 1);

    const displayX = clamp(planeXRef.current + tweak.leadX, 0, dimension.width);

    const baseAtX = curveFunction(displayX, {
      width: dimension.width,
      height: dimension.height,
    });

    const xOnCurve = sxBase * displayX + crashX;
    const yOnCurve =
      dimension.height - syBase * baseAtX - crashY + (tweak.leadY ?? 0);

    posPlaneRef.current = { x: xOnCurve, y: yOnCurve };

    const planeXOffset =
      stage === GameStages.RUN ? tweak.xOffsetRun : tweak.xOffsetWait;

    const rawPlaneSpriteX = posPlaneRef.current.x + planeXOffset;
    const rawPlaneSpriteY = posPlaneRef.current.y - tweak.yOffset;

    let planeSpriteX = rawPlaneSpriteX;
    let planeSpriteY = rawPlaneSpriteY;

    if (stage === GameStages.RUN) {
      const maxX = dimension.width * tweak.planeMaxX;
      planeSpriteX = Math.min(rawPlaneSpriteX, maxX);
      planeSpriteY = Math.max(tweak.planeMinY, rawPlaneSpriteY);
    }

    const planeSprite = planeSpriteRef.current;
    if (planeSprite) {
      planeSprite.position.set(planeSpriteX, planeSpriteY);
    }

    const canApplyEnd = displayX >= tweak.endMinX;

    const safePlaneX = Math.max(displayX, 1);
    const safeBaseAtX = Math.max(baseAtX, 1);

    const endExtendX = canApplyEnd ? tweak.endX : 0;
    const endExtendY = canApplyEnd ? tweak.endY : 0;

    const extraScaleX = endExtendX / safePlaneX;
    const extraScaleY = endExtendY / safeBaseAtX;

    const sxDynamic = sxBase + extraScaleX;
    const syDynamic = syBase + extraScaleY;

    const curveG = curveRef.current;
    if (curveG) {
      curveG.position.set(0, dimension.height);
      curveG.pivot.set(0, dimension.height);
      curveG.scale.set(sxDynamic, syDynamic);
    }

    const maskG = maskRef.current;
    if (maskG) {
      maskG.position.set(0, 0);
      const maskScaleX = (sxDynamic * displayX) / Math.max(dimension.width, 1);
      maskG.scale.set(maskScaleX, 1);
    }
  });

  const drawRadialSlices = (g: GraphicsRaw) => {
    g.clear();
    const centerX = 0;
    const centerY = 0;
    const totalSlices = 60;
    const radius = Math.max(dimension.width, dimension.height) * 2;

    const primaryColorNum = 0x0a0a0a;
    const secondaryColorNum = 0x121212;

    for (let i = 0; i < totalSlices; i++) {
      const startAngle = (i / totalSlices) * Math.PI * 2;
      const endAngle = ((i + 1) / totalSlices) * Math.PI * 2;
      g.beginFill(i % 2 === 0 ? primaryColorNum : secondaryColorNum);
      g.moveTo(centerX, centerY);
      g.arc(centerX, centerY, radius, startAngle, endAngle);
      g.lineTo(centerX, centerY);
      g.endFill();
    }
  };

  const payoutTextStyle = useMemo(
    () =>
      new TextStyle({
        align: "center",
        fontFamily: "Roboto",
        fontSize: bp === "mobile" || bp === "mobileSmall" ? 350 : 150,
        fontWeight: "700",
        fill: ["#ffffff", "#ffffff"],
        stroke: "#111111",
        strokeThickness: 2,
        dropShadow: false,
      }),
    [bp],
  );

  // ⚙️ Animation toggle: keep plane visible for crash while hiding the curve
  const showCurve = animationEnabled && game_anim_status === GameStages.RUN;
  const showPlane =
    animationEnabled &&
    (game_anim_status === GameStages.RUN ||
      game_anim_status === GameStages.BLAST);

  return (
    <Container>
      {/* 🌞 Background rays (always visible, rotate only in RUN) */}
      <Container
        ref={raysRef}
        x={0}
        y={dimension.height}
        pivot={{ x: 0, y: 0 }}
      >
        <Graphics draw={drawRadialSlices} />
      </Container>

      {/* 🌈 Multiplier gradient (keep original behavior: only by RUN stage) */}
      {game_anim_status === GameStages.RUN ? (
        <Sprite
          key={gradTint}
          tint={gradTint}
          alpha={0.6}
          texture={gradTexture}
          width={dimension.width}
          height={dimension.height}
          x={0}
          y={0}
        />
      ) : null}

      {/* Corner glow (always visible like original) */}
      <Graphics
        draw={(g) => {
          g.clear();
          g.beginFill(0xffd700, 0.3);
          g.drawCircle(0, 0, 100);
          g.endFill();
        }}
        x={-100}
        y={dimension.height + 100}
      />

      {/* line + plane together */}
      <Container x={tweak.graphOffset.x} y={tweak.graphOffset.y}>
        {/* Board mask */}
        <Graphics
          ref={boardMaskRef as any}
          draw={dotLeftBottom}
          x={0}
          scale={{ x: dimension.width, y: dimension.height }}
        />

        {/* ✅ Curve + filled area (HIDDEN when animation OFF) */}
        <Container ref={revealRef} visible={showCurve} x={0} y={0}>
          <Graphics
            ref={curveRef as any}
            draw={renderCurve}
            pivot={{ x: 0, y: dimension.height }}
          />
          <Graphics ref={maskRef as any} name="mask" draw={curveMaskDraw} />
        </Container>

        {/* ✅ Plane (HIDDEN when animation OFF) */}
        <Sprite
          ref={planeSpriteRef as any}
          visible={showPlane}
          texture={planeFrames[currentFrame]}
          pivot={{ x: 0.08, y: 0.54 }}
          anchor={tweak.anchor}
          scale={planeScale}
          x={0}
          y={0}
        />
      </Container>

      {/* 🧮 Payout text (keep original behavior: show in RUN only) */}
      <Text
        visible={game_anim_status === GameStages.RUN}
        text={payout.toFixed(2) + "x"}
        anchor={0.5}
        x={dimension.width / 2}
        y={dimension.height / 2}
        style={payoutTextStyle}
      />
    </Container>
  );
};

export default AppStage;
